import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Chip, CircularProgress, IconButton, LinearProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { Check as CheckIcon, Clear as ClearIcon } from '@mui/icons-material';
import moment from 'moment/moment';
import deepmerge from 'deepmerge';

import { DeleteButton, DuplicateButton } from '../../../common/button';
import { GET_ROUTE_PROMOTION_CAMPAIGNS_FORMS, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT, ROUTE_PROMOTION_CAMPAIGNS_FORMS_NEW } from '../../../../core/constants';
import { getPromotionCampaignsAction, getPromotionCampaignsState } from '../../../../features/price/get_promotion_campaigns';
import { getButtonText, getPromotionCampaignStatus, getUpdatePromotionCampaignStatus } from '../../../common/utils/promotion_campaign_utils';
import { getPromotionCampaignsNotificationData, updatePromotionCampaignStatusNotificationData } from '../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import useNotification from '../../../../core/hooks/use_notification';
import PaginationControls from '../../../common/pagination_controls';
import { updatePromotionCampaignStatusAction } from '../../../../features/price/update_promotion_campaign_status';
import { PromotionCampaignStatusEnum } from '../../../../core/enums/price/promotion_campaign';
import { FormRouteEnum } from '../../../../core/enums/common/route';

const ActivePromotionCampaignsList = ({ tab, queryParams, setQueryParams }) => {
    const { showErrorMessage, showNotification } = useNotification();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const getPromotionCampaigns = useSelector(getPromotionCampaignsState);

    const [updatingStatuses, setUpdatingStatuses] = useState({});

    const refreshPromotionCampaignList = useCallback(() => {
        dispatch(getPromotionCampaignsAction(queryParams))
            .then(response => showErrorMessage(getPromotionCampaignsNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    useEffect(() => {
        refreshPromotionCampaignList();
    }, [queryParams, refreshPromotionCampaignList]);

    const onUpdatePromotionCampaignStatus = (promotionCampaign, status = null) => {
        setUpdatingStatuses(prev => ({ ...prev, [promotionCampaign?.id]: true }));
        const promotionCampaignStatus = status || getUpdatePromotionCampaignStatus(promotionCampaign?.status);

        dispatch(updatePromotionCampaignStatusAction({ id: promotionCampaign?.id, status: promotionCampaignStatus }))
            .then(response => {
                showNotification(updatePromotionCampaignStatusNotificationData(response, promotionCampaignStatus));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshPromotionCampaignList();
            })
            .finally(() => {
                setUpdatingStatuses(prev => ({ ...prev, [promotionCampaign?.id]: false }));
            });
    };

    const getStatusChipColor = (status) => {
        switch (status) {
            case PromotionCampaignStatusEnum.ACTIVE:
            case 'ACTIVE':
                return 'success';
            case PromotionCampaignStatusEnum.DRAFT:
            case 'DRAFT':
                return 'warning';
            default:
                return 'default';
        }
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
            { getPromotionCampaigns?.loading && <LinearProgress/> }

            {
                getPromotionCampaigns?.data && (
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell>Nazwa robocza</TableCell>
                                    <TableCell align="center">Data obowiązywania</TableCell>
                                    <TableCell align="center">Powtarzalna</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="right" sx={ { pr: 3 } }></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    getPromotionCampaigns?.data?.content?.map(promotionCampaign => {
                                        const isUpdating = updatingStatuses[promotionCampaign?.id] || false;
                                        const isActive = promotionCampaign?.status === PromotionCampaignStatusEnum.ACTIVE || promotionCampaign?.status === 'ACTIVE';

                                        return (
                                            <TableRow key={ promotionCampaign?.id } hover>
                                                <TableCell
                                                    sx={ { fontWeight: 500 } }>{ promotionCampaign?.workingName }</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={ {
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center'
                                                    } }>
                                                        <Typography variant="body2">
                                                            Od:&nbsp;{ moment(promotionCampaign?.timeFrom, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Do:&nbsp;{ moment(promotionCampaign?.timeTo, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    { promotionCampaign?.repeatable ?
                                                        <CheckIcon color="success" fontSize="small"/> :
                                                        <ClearIcon color="error" fontSize="small"/> }
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={ getPromotionCampaignStatus(promotionCampaign?.status) }
                                                        color={ getStatusChipColor(promotionCampaign?.status) }
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="right" sx={ { pr: 2 } }>
                                                    <Stack direction="row" spacing={ 1 } justifyContent="flex-end"
                                                           alignItems="center">
                                                        <Tooltip title={ getButtonText(promotionCampaign?.status) }>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color={ isActive ? 'error' : 'success' }
                                                                    onClick={ () => onUpdatePromotionCampaignStatus(promotionCampaign) }
                                                                    disabled={ isUpdating }
                                                                >
                                                                    { isUpdating ? <CircularProgress size={ 20 }
                                                                                                     color="inherit"/> :
                                                                        <PowerSettingsNewIcon fontSize="small"/> }
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <DuplicateButton size="small"
                                                                         onClick={ () => navigate(ROUTE_PROMOTION_CAMPAIGNS_FORMS_NEW, {
                                                                             state: {
                                                                                 action: FormRouteEnum.NEW,
                                                                                 promotionCampaignIdToDuplicate: promotionCampaign?.id,
                                                                                 queryParams,
                                                                                 tab
                                                                             }
                                                                         }) }/>
                                                        <Tooltip title="Edytuj">
                                                            <IconButton size="small" color="primary"
                                                                        onClick={ () => navigate(GET_ROUTE_PROMOTION_CAMPAIGNS_FORMS(promotionCampaign?.id), {
                                                                            state: {
                                                                                action: FormRouteEnum.EDIT,
                                                                                queryParams,
                                                                                tab
                                                                            }
                                                                        }) }>
                                                                <EditIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                        <DeleteButton size="small"
                                                                      onDeleteClick={ () => onUpdatePromotionCampaignStatus(promotionCampaign, PromotionCampaignStatusEnum.DELETED) }
                                                                      isLoading={ isUpdating }/>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                )
            }
            {
                getPromotionCampaigns?.data && (
                    <Box sx={ { mt: 2, display: 'flex', justifyContent: 'center' } }>
                        <PaginationControls
                            currentPage={ getPromotionCampaigns?.data?.pageable?.pageNumber }
                            totalPages={ getPromotionCampaigns?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                )
            }
        </Box>
    );
};

export default ActivePromotionCampaignsList;